import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Network } from 'lucide-react';
import api from '../lib/api';
import { integrationService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { Integration, IntegrationFilterOptions } from '../types/integration';
import type { Assistant } from '../types/api';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import SearchInput from '../components/ui/SearchInput';
import IntegrationList from '../components/integrations/IntegrationList';
import IntegrationForm from '../components/integrations/IntegrationForm';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/ui/LoadingSpinner';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

const DEFAULT_LIMIT = 20;

function Integrations() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [filters, setFilters] = useState<IntegrationFilterOptions>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: integrationsResponse, isLoading } = useQuery({
    queryKey: ['integrations', filters],
    queryFn: () => integrationService.getIntegrations(filters),
    keepPreviousData: true,
  });

  // Query to get assistants for deletion verification
  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: async () => {
      const response = await api.get('/assistants');
      return response.data;
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Integration>) => integrationService.createIntegration(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      messageHandler.success('Integração criada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Integration) => integrationService.updateIntegration(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      messageHandler.success('Integração atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => integrationService.deleteIntegration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      messageHandler.success('Integração excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Helper function to process array fields from FormData
  const processArrayField = (formData: FormData, fieldName: string): number[] | undefined => {
    const stringValue = formData.get(fieldName) as string;
    if (stringValue) {
      return stringValue
        .split(',')
        .map(item => parseInt(item.trim(), 10))
        .filter(num => !isNaN(num));
    }

    const entries = Array.from(formData.entries())
      .filter(([key]) => key.startsWith(`${fieldName}[`))
      .map(([_, value]) => parseInt(value as string, 10))
      .filter(num => !isNaN(num));

    return entries.length > 0 ? entries : undefined;
  };

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      description: formData.get('description') as string,
      ixc_url: (formData.get('ixc_url') as string) || '',
      ixc_token: (formData.get('ixc_token') as string) || '',
      gammu_user: (formData.get('gammu_user') as string) || '',
      gammu_pwd: (formData.get('gammu_pwd') as string) || '',
      ticketz_url: (formData.get('ticketz_url') as string) || '',
      ticketz_token: (formData.get('ticketz_token') as string) || '',
      ticketz_companyId: Number(formData.get('ticketz_companyId')) || 0,
      ticketz_userId: Number(formData.get('ticketz_userId')) || 0,
      ixc_id_assunto: Number(formData.get('ixc_id_assunto')) || 0,
      ixc_id_tecnico: Number(formData.get('ixc_id_tecnico')) || 0,
      ixc_id_atendente: Number(formData.get('ixc_id_atendente')) || 0,
      ixc_id_filial: Number(formData.get('ixc_id_filial')) || 0,
      ixc_id_setor: Number(formData.get('ixc_id_setor')) || 0,
      digisac_account: (formData.get('digisac_account') as string) || '',
      digisac_url: (formData.get('digisac_url') as string) || '',
      digisac_token: (formData.get('digisac_token') as string) || '',
      sgp_url: (formData.get('sgp_url') as string) || '',
      sgp_token: (formData.get('sgp_token') as string) || '',
      sgp_app: (formData.get('sgp_app') as string) || '',
      hubsoft_url: (formData.get('hubsoft_url') as string) || '',
      hubsoft_client_id: Number(formData.get('hubsoft_client_id')) || 0,
      hubsoft_client_secret: (formData.get('hubsoft_client_secret') as string) || '',
      hubsoft_username: (formData.get('hubsoft_username') as string) || '',
      hubsoft_password: (formData.get('hubsoft_password') as string) || '',
      hubsoft_token: (formData.get('hubsoft_token') as string) || '',
      sgp_ocorrenciatipo: Number(formData.get('sgp_ocorrenciatipo')) || 0,
      sgp_setor: Number(formData.get('sgp_setor')) || 0,
      mediawiki_url: (formData.get('mediawiki_url') as string) || '',
      mediawiki_username: (formData.get('mediawiki_username') as string) || '',
      mediawiki_password: (formData.get('mediawiki_password') as string) || '',
      opa_url: (formData.get('opa_url') as string) || '',
      opa_token: (formData.get('opa_token') as string) || '',
      opa_ssh_host: (formData.get('opa_ssh_host') as string) || '',
      opa_ssh_port: Number(formData.get('opa_ssh_port')) || 0,
      opa_ssh_user: (formData.get('opa_ssh_user') as string) || '',
      opa_ssh_passwd: (formData.get('opa_ssh_passwd') as string) || '',
      hubsoft_id_tipo_atendimento: Number(formData.get('hubsoft_id_tipo_atendimento')) || 0,
      fortics_url: (formData.get('fortics_url') as string) || '',
      fortics_user: (formData.get('fortics_user') as string) || '',
      fortics_password: (formData.get('fortics_password') as string) || '',
      fortics_token: (formData.get('fortics_token') as string) || '',
      asaas_url: (formData.get('asaas_url') as string) || '',
      asaas_access_token: (formData.get('asaas_access_token') as string) || '',
      asaas_evp_key: (formData.get('asaas_evp_key') as string) || '',
      generic_url: (formData.get('generic_url') as string) || '',
      generic_token: (formData.get('generic_token') as string) || '',
      ixc_assunto_os_finished: processArrayField(formData, 'ixc_assunto_os_finished'),
      ixc_assunto_os_new_contract: processArrayField(formData, 'ixc_assunto_os_new_contract'),
      ixc_assunto_os_cancellation: processArrayField(formData, 'ixc_assunto_os_cancellation'),
      ixc_assunto_os_budget: processArrayField(formData, 'ixc_assunto_os_budget'),
    };

    if (editingIntegration) {
      updateMutation.mutate({ ...editingIntegration, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (integration: Integration) => {
    // Check if the integration is being used by any assistant
    const linkedAssistants = assistants?.filter(
      (assistant: Assistant) => assistant.integration_id === integration.id
    );

    if (linkedAssistants && linkedAssistants.length > 0) {
      messageHandler.alert(
        'Integração em uso',
        `Esta integração está sendo utilizada por ${linkedAssistants.length} assistente(s). Remova a vinculação dos assistentes antes de excluir a integração.`,
        'error'
      );
      return;
    }

    messageHandler.confirm(
      'Excluir Integração',
      'Tem certeza que deseja excluir esta integração?',
      () => deleteMutation.mutate(integration.id)
    );
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value || undefined, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingIntegration(null);
  };

  const integrations = integrationsResponse?.data || [];
  const pagination = integrationsResponse?.pagination;
  const totalRecords = pagination?.total || 0;

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Integrações"
          description="Lista de todas as integrações configuradas."
          actions={
            <Button
              onClick={() => {
                setEditingIntegration(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Integração
            </Button>
          }
        />

        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <SearchInput
            onSearch={handleSearch}
            placeholder="Pesquisar por descrição..."
            className="sm:max-w-xs"
          />
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <Network className="h-4 w-4 mr-2" />
            <span>
              Total de registros: <strong>{totalRecords}</strong>
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : integrations.length > 0 ? (
          <>
            <IntegrationList
              integrations={integrations}
              onEdit={integration => {
                setEditingIntegration(integration);
                setModal({ type: 'edit' });
              }}
              onDelete={handleDelete}
            />

            {pagination && pagination.last_page > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={<Network className="h-12 w-12" />}
            title="Nenhuma integração configurada"
            description="Configure sua primeira integração para começar"
            action={{
              label: 'Criar Integração',
              onClick: () => {
                setEditingIntegration(null);
                setModal({ type: 'create' });
              },
            }}
          />
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingIntegration ? 'Editar Integração' : 'Nova Integração'}
          size="xl"
        >
          <IntegrationForm
            integration={editingIntegration}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Integrations;
